import './App.css';
import { useEffect, useRef, useState } from 'react';
import siteConfig from './config/siteConfig';
import cities from './data/cities.json';
import CookieConsent, { openCookieSettings } from './components/CookieConsent';
import { trackContactConversion } from './utils/consent';
import {
  FaBars,
  FaCarCrash,
  FaCarSide,
  FaCheckCircle,
  FaClock,
  FaArrowUp,
  FaEnvelope,
  FaInstagram,
  FaPhoneAlt,
  FaSearchDollar,
  FaTimes,
  FaWhatsapp,
  FaMapMarkerAlt
} from 'react-icons/fa';

function App() {
  const { siteUrl, companyName, contact, hero, about, socialLinks, web3forms, legal } = siteConfig;
  const cityRoutePrefix = '/kfz-gutachten-';
  const normalizedPathname =
    window.location.pathname.replace(/\/+$/, '') || '/';
  const pathCitySlug = normalizedPathname.startsWith(cityRoutePrefix)
    ? normalizedPathname.slice(cityRoutePrefix.length).toLowerCase()
    : '';
  const defaultCity = cities[0];
  const activeCity = cities.find((city) => city.slug === pathCitySlug) || defaultCity;
  const formatCityList = (items) => {
    if (items.length <= 1) {
      return items[0] || '';
    }
    if (items.length === 2) {
      return `${items[0]} und ${items[1]}`;
    }
    return `${items.slice(0, -1).join(', ')} und ${items[items.length - 1]}`;
  };
  const cityListText = formatCityList(cities.map((city) => city.name));
  const phoneNumber = contact.phoneLink;
  const whatsappNumber = contact.whatsappLink;
  const heroBackgroundImage = hero.backgroundImageUrl;
  const [selectedService, setSelectedService] = useState('');
  const [formSubmitState, setFormSubmitState] = useState({ status: 'idle', message: '' });
  const lastScrollYRef = useRef(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    lastScrollYRef.current = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastScrollYRef.current;
      if (delta > 4) {
        document.documentElement.dataset.scroll = 'down';
      } else if (delta < -4) {
        document.documentElement.dataset.scroll = 'up';
      }
      lastScrollYRef.current = y;
    };
    document.documentElement.dataset.scroll = 'down';
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const revealElements = document.querySelectorAll('.reveal');
    if (!revealElements.length) {
      return undefined;
    }

    // Fallback: reveal immediately when IntersectionObserver is unavailable.
    if (!('IntersectionObserver' in window)) {
      revealElements.forEach((el) => el.classList.add('reveal-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );

    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2);

    const animateScrollTo = (targetY, duration = 850) => {
      const startY = window.scrollY;
      const distance = targetY - startY;
      const startTime = performance.now();

      const step = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeInOutCubic(progress);
        window.scrollTo(0, startY + distance * easedProgress);

        if (progress < 1) {
          requestAnimationFrame(step);
        }
      };

      requestAnimationFrame(step);
    };

    const anchorLinks = Array.from(document.querySelectorAll('a[href^="#"]'));
    const onClickHandlers = [];

    anchorLinks.forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || href === '#') {
        return;
      }

      const handler = (event) => {
        const targetElement = document.querySelector(href);
        if (!targetElement) {
          return;
        }

        event.preventDefault();

        const topbar = document.querySelector('.topbar');
        const topbarOffset = topbar ? topbar.getBoundingClientRect().height : 0;
        const targetY = targetElement.getBoundingClientRect().top + window.scrollY - topbarOffset - 10;
        animateScrollTo(Math.max(targetY, 0));
      };

      link.addEventListener('click', handler);
      onClickHandlers.push({ link, handler });
    });

    return () => {
      onClickHandlers.forEach(({ link, handler }) => link.removeEventListener('click', handler));
    };
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 980) {
        setMenuOpen(false);
      }
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const aboutSection = document.querySelector('#ueber-uns');
      if (!aboutSection) {
        setShowBackToTop(window.scrollY > 320);
        return;
      }

      const aboutTop = aboutSection.offsetTop;
      setShowBackToTop(window.scrollY > aboutTop - 120);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  useEffect(() => {
    const sections = document.querySelectorAll('main > section');
    if (!sections.length) {
      return undefined;
    }

    sections.forEach((section) => section.classList.add('section-fade-track'));

    if (!('IntersectionObserver' in window)) {
      sections.forEach((section) => section.classList.add('section-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('section-visible');
          } else {
            entry.target.classList.remove('section-visible');
          }
        });
      },
      {
        threshold: 0.22,
        rootMargin: '-8% 0px -8% 0px'
      }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const resolvedSiteUrl = siteUrl.replace(/\/+$/, '');
    const canonicalPath = normalizedPathname === '/' ? '/' : normalizedPathname;
    const canonicalUrl = `${resolvedSiteUrl}${canonicalPath}`;
    const pageTitle = `KFZ-Gutachten ${activeCity.name} | Unfall- & Schadengutachten`;
    const pageDescription = `Unabhängiger KFZ-Gutachter in ${activeCity.name}: Unfallgutachten, Schadengutachten und Wertgutachten. Schnelle Termine und persönliche Beratung.`;
    const pageKeywords = [
      `kfz gutachten ${activeCity.name.toLowerCase()}`,
      `unfallgutachten ${activeCity.name.toLowerCase()}`,
      `schadengutachten ${activeCity.name.toLowerCase()}`,
      'kfz sachverständiger',
      'wertgutachten'
    ].join(', ');

    const upsertMeta = (selector, attrs, content) => {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        Object.entries(attrs).forEach(([key, value]) => element.setAttribute(key, value));
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const upsertLink = (selector, attrs) => {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement('link');
        document.head.appendChild(element);
      }
      Object.entries(attrs).forEach(([key, value]) => element.setAttribute(key, value));
    };

    document.title = pageTitle;
    upsertMeta('meta[name="description"]', { name: 'description' }, pageDescription);
    upsertMeta('meta[name="keywords"]', { name: 'keywords' }, pageKeywords);
    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, pageTitle);
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, pageDescription);
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl);
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, pageTitle);
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, pageDescription);
    upsertLink('link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl });

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: companyName,
      url: canonicalUrl,
      email: contact.email,
      telephone: contact.phoneDisplay,
      address: {
        '@type': 'PostalAddress',
        streetAddress: contact.street,
        postalCode: contact.postalCode,
        addressLocality: contact.city,
        addressCountry: 'DE'
      },
      areaServed: cities.map((city) => city.name),
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '08:00',
          closes: '20:00'
        }
      ]
    };

    let jsonLdScript = document.head.querySelector('script[data-seo="localbusiness"]');
    if (!jsonLdScript) {
      jsonLdScript = document.head.querySelector('script[type="application/ld+json"]');
    }
    if (!jsonLdScript) {
      jsonLdScript = document.createElement('script');
      jsonLdScript.setAttribute('type', 'application/ld+json');
      document.head.appendChild(jsonLdScript);
    }
    jsonLdScript.setAttribute('data-seo', 'localbusiness');
    jsonLdScript.textContent = JSON.stringify(jsonLd);
  }, [activeCity.name, companyName, contact, normalizedPathname, siteUrl]);

  const services = [
    {
      icon: <FaCarCrash />,
      title: 'Unfall- & Schadengutachten',
      primary: true,
      description:
        'Unsere Hauptdienstleistung: Präzise Schadenaufnahme mit Fotodokumentation und kalkulierter Reparaturkostenbasis für die Regulierung.'
    },
    {
      icon: <FaSearchDollar />,
      title: 'Wertgutachten',
      description:
        'Professionelle Marktwert- und Wiederbeschaffungswertermittlung für PKW, Youngtimer und Oldtimer.'
    },
    {
      icon: <FaCarSide />,
      title: 'Young- & Oldtimergutachten',
      description:
        'Fachgerechte Begutachtung spezieller Fahrzeuge mit Fokus auf Zustand, Historie und Werterhalt.'
    },
    {
      icon: <FaCheckCircle />,
      title: 'Kurzgutachten / Kostenvoranschlag',
      description:
        'Kompakte und nachvollziehbare Einschätzung bei kleineren Schäden oder zur ersten Orientierung.'
    }
  ];

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setFormSubmitState({ status: 'submitting', message: '' });

    const formData = new FormData(form);
    formData.append('access_key', web3forms.accessKey);
    formData.append('subject', `Kontaktanfrage Website – ${companyName}`);

    const vorname = (formData.get('vorname') || '').toString().trim();
    const nachname = (formData.get('nachname') || '').toString().trim();
    if (vorname || nachname) {
      formData.append('name', [vorname, nachname].filter(Boolean).join(' '));
    }

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        setFormSubmitState({
          status: 'success',
          message: 'Vielen Dank! Ihre Anfrage wurde gesendet. Wir melden uns bald bei Ihnen.'
        });
        form.reset();
        setSelectedService('');
        trackContactConversion();
      } else {
        setFormSubmitState({
          status: 'error',
          message:
            (data.message && String(data.message)) ||
            'Senden fehlgeschlagen. Bitte versuchen Sie es erneut oder kontaktieren Sie uns telefonisch.'
        });
      }
    } catch {
      setFormSubmitState({
        status: 'error',
        message: 'Netzwerkfehler. Bitte versuchen Sie es später erneut oder rufen Sie uns an.'
      });
    }
  };

  return (
    <>
      <div className="site">
        {menuOpen && <button className="nav-overlay" aria-label="Menü schließen" onClick={() => setMenuOpen(false)} />}
        <a className="skip-link" href="#start">
          Zum Inhalt springen
        </a>

      <header className="topbar">
        <div className="container nav-wrap">
          <a href="#start" className="brand" aria-label="Startseite">
            <img
              src={`${process.env.PUBLIC_URL}/images/logo.png`}
              alt={`${companyName} Logo`}
              className="brand-logo"
            />
          </a>
          <button
            className="menu-toggle"
            type="button"
            aria-label={menuOpen ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={menuOpen}
            aria-controls="hauptnavigation"
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            {menuOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
          </button>
          <nav
            id="hauptnavigation"
            aria-label="Hauptnavigation"
            className={`nav ${menuOpen ? 'nav-open' : ''}`}
          >
            <a href="#start" className="nav-logo-link" onClick={() => setMenuOpen(false)} aria-label="Startseite">
              <img
                src={`${process.env.PUBLIC_URL}/images/logo.png`}
                alt={`${companyName} Logo`}
                className="nav-logo"
              />
            </a>
            <a href="#start" onClick={() => setMenuOpen(false)}>Start</a>
            <a href="#ueber-uns" onClick={() => setMenuOpen(false)}>Über Uns</a>
            <a href="#leistungen" onClick={() => setMenuOpen(false)}>Leistungen</a>
            <a href="#kontakt" onClick={() => setMenuOpen(false)}>Kontakt</a>
          </nav>
        </div>
      </header>

      <main>
        <section
          id="start"
          className="hero"
          style={{ '--hero-bg-image': `url(${heroBackgroundImage})` }}
        >
          <div className="container">
            <div className="hero-simple reveal">
              <div className="hero-content reveal" style={{ '--reveal-delay': '80ms' }}>
                <h1 className="hero-headline">
                  Ihr KFZ-Gutachter in<br />
                  <span className="hero-accent">{activeCity.name}</span>
                </h1>
                <h2 className="hero-subheadline">
                  Ich komme zu <span className="hero-accent">Ihnen und Kostenlos</span>
                </h2>
                <p className="hero-text">
                  Wir dokumentieren Ihren Schaden professionell und begleiten Sie
                  verlässlich von der Erstaufnahme bis zum fertigen Gutachten.
                </p>
                <div className="hero-quick-facts" aria-label="Schnellinfos">
                  <span><FaClock aria-hidden="true" /> Schnelle Terminvergabe</span>
                  <span><FaMapMarkerAlt aria-hidden="true" /> {activeCity.name} und Umgebung</span>
                </div>
                <div className="cta-row">
                  <a className="btn btn-primary" href={`tel:${phoneNumber}`}>
                    <FaPhoneAlt aria-hidden="true" /> Jetzt Erstgespräch
                  </a>
                  <a
                    className="btn btn-whatsapp"
                    href={`https://wa.me/${whatsappNumber}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FaWhatsapp aria-hidden="true" /> Schaden per WhatsApp senden
                  </a>
                </div>
              </div>
              <div className="hero-panel reveal" style={{ '--reveal-delay': '180ms' }}>
                <h5 className="hero-panel-label">Schnelle Hilfe im Schadenfall</h5>
                <h3>Unfall passiert? Wir starten sofort.</h3>
                <div className="hero-stats" aria-label="Leistungsmerkmale">
                  <article>
                    <strong>Unabhängig</strong>
                    <span>Neutral und rechtssicher</span>
                  </article>
                  <article>
                    <strong>Vor Ort</strong>
                    <span>Flexible Termine in der Region</span>
                  </article>
                  <article>
                    <strong>Digital</strong>
                    <span>Bilder & Infos direkt per WhatsApp</span>
                  </article>
                </div>
                <a className="btn btn-light btn-full" href="#kontakt">
                  Unverbindlich anfragen
                </a>
              </div>
            </div>
          </div>
        </section>

        <section id="ueber-uns" className="section">
          <div className="container about-modern">
            <div className="about-modern-card">
              <h5 className="eyebrow">Über Uns</h5>
              <h3>Gutachten, die Klarheit schaffen</h3>
              <p className="about-modern-text">
                Wir erstellen unabhängige Unfall- und Schadengutachten in {activeCity.name} und
                Umgebung - präzise dokumentiert, verständlich erklärt und persönlich begleitet.
              </p>
              <div className="about-modern-points" aria-label="Vorteile">
                <article>
                  <strong>Schnell vor Ort</strong>
                  <span>Termine oft kurzfristig möglich</span>
                </article>
                <article>
                  <strong>Rechtssicher</strong>
                  <span>Neutral und sauber dokumentiert</span>
                </article>
              </div>
            </div>
            <div className="about-modern-image">
              <img className="about-image" src={about.imageUrl} alt={about.imageAlt} loading="lazy" />
            </div>
          </div>
        </section>

        <section id="leistungen" className="section section-alt">
          <div className="container">
            <h3 className="reveal">Unsere Leistungen</h3>
            <h5 className="section-intro reveal" style={{ '--reveal-delay': '80ms' }}>
              Unsere Kernleistungen für Privat- und Geschäftskunden in der Region {cityListText}.
            </h5>
            <div className="services-grid">
              {services.map((service, index) => (
                <article
                  className={`service-card reveal ${service.primary ? 'service-card-primary' : ''}`}
                  key={service.title}
                  style={{ '--reveal-delay': `${index * 90}ms` }}
                >
                  <div className="service-icon" aria-hidden="true">
                    {service.icon}
                  </div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <a className="btn btn-primary service-cta" href="#kontakt">
                    Jetzt anfragen
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="kontakt" className="section">
          <div className="container contact-box reveal">
            <div>
              <h3>Kontakt</h3>
              <p>
                Sie benötigen kurzfristig ein KFZ-Gutachten in {activeCity.name} und Umgebung? Kontaktieren
                Sie uns telefonisch oder per WhatsApp für eine schnelle Erstberatung.
              </p>
              <div className="contact-meta" aria-label="Kontaktinformationen">
                <div className="contact-meta-item">
                  <FaMapMarkerAlt aria-hidden="true" />
                  <div>
                    <p className="contact-meta-label">Einsatzgebiet</p>
                    <div className="contact-area-badges">
                      <span>Ganz Nordrhein-Westfalen</span>
                    </div>
                  </div>
                </div>
                <div className="contact-meta-item">
                  <FaClock aria-hidden="true" />
                  <div>
                    <p className="contact-meta-label">Erreichbarkeit</p>
                    <p>24 Stunden erreichbar (Telefon/WhatsApp)</p>
                  </div>
                </div>
                <div className="contact-meta-item">
                  <FaEnvelope aria-hidden="true" />
                  <div>
                    <p className="contact-meta-label">E-Mail</p>
                    <p>{contact.email}</p>
                  </div>
                </div>
              </div>
              <div className="opening-hours" aria-label="Büro-Öffnungszeiten">
                <h5 className="opening-hours-title">Büro-Öffnungszeiten</h5>
                <div className="opening-hours-row">
                  <span>Montag – Freitag</span>
                  <strong>08:00 – 20:00</strong>
                </div>
                <div className="opening-hours-row">
                  <span>Samstag</span>
                  <strong>08:00 – 20:00</strong>
                </div>
                <div className="opening-hours-row opening-hours-row-closed">
                  <span>Sonntag & Feiertage</span>
                  <strong>Geschlossen</strong>
                </div>
              </div>
              <div className="cta-column cta-column-inline">
                <a className="btn btn-primary btn-full" href={`tel:${phoneNumber}`}>
                  <FaPhoneAlt aria-hidden="true" /> Telefon: {contact.phoneDisplay}
                </a>
                <a
                  className="btn btn-whatsapp btn-full"
                  href={`https://wa.me/${whatsappNumber}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FaWhatsapp aria-hidden="true" /> WhatsApp: {contact.whatsappDisplay}
                </a>
              </div>
            </div>
            <form className="contact-form" onSubmit={handleContactSubmit}>
              <div className="form-grid-two">
                <div className="form-row">
                  <label htmlFor="vorname">Vorname</label>
                  <input id="vorname" name="vorname" type="text" required />
                </div>
                <div className="form-row">
                  <label htmlFor="nachname">Nachname</label>
                  <input id="nachname" name="nachname" type="text" required />
                </div>
              </div>
              <div className="form-row">
                <label htmlFor="email">E-Mail</label>
                <input id="email" name="email" type="email" required />
              </div>
              <div className="form-row">
                <label htmlFor="telefon">Telefon</label>
                <input id="telefon" name="telefon" type="tel" required />
              </div>
              <div className="form-row">
                <p className="form-label">Leistung wählen</p>
                <div className="service-options" role="listbox" aria-label="Leistung wählen">
                  {[
                    'Unfall- & Schadengutachten',
                    'Wertgutachten',
                    'Young- & Oldtimergutachten',
                    'Kurzgutachten / Kostenvoranschlag'
                  ].map((service) => (
                    <button
                      type="button"
                      key={service}
                      className={`service-option ${selectedService === service ? 'active' : ''}`}
                      aria-pressed={selectedService === service}
                      onClick={() => setSelectedService(service)}
                    >
                      {service}
                    </button>
                  ))}
                </div>
                <input type="hidden" name="leistung" value={selectedService} required />
              </div>
              <div className="form-row">
                <label htmlFor="nachricht">Nachricht</label>
                <textarea id="nachricht" name="nachricht" rows="5" required />
              </div>
              <div className="form-row form-consent">
                <label className="consent-label" htmlFor="datenschutz">
                  <input id="datenschutz" name="datenschutz" type="checkbox" required />
                  <span className="consent-check" aria-hidden="true" />
                  <span className="consent-text">
                    Ich akzeptiere die Verarbeitung meiner Daten gemäß der{' '}
                    <a
                      href={legal.datenschutzUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Datenschutzerklärung
                    </a>
                    .
                  </span>
                </label>
              </div>
              <button
                className="btn btn-primary btn-full"
                type="submit"
                disabled={formSubmitState.status === 'submitting'}
              >
                {formSubmitState.status === 'submitting' ? 'Wird gesendet…' : 'Anfrage senden'}
              </button>
              {formSubmitState.message ? (
                <p
                  className={`form-feedback ${formSubmitState.status === 'success' ? 'form-feedback-success' : 'form-feedback-error'}`}
                  role="status"
                  aria-live="polite"
                >
                  {formSubmitState.message}
                </p>
              ) : null}
            </form>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container">
          <p>
            © {new Date().getFullYear()} {companyName} - Alle Rechte vorbehalten.
          </p>
          <nav className="footer-legal" aria-label="Rechtliches">
            <a href={legal.impressumUrl}>Impressum</a>
            <span className="footer-legal-sep" aria-hidden="true">
              ·
            </span>
            <a href={legal.datenschutzUrl}>Datenschutzerklärung</a>
            <span className="footer-legal-sep" aria-hidden="true">
              ·
            </span>
            <button type="button" className="footer-legal-button" onClick={openCookieSettings}>
              Cookie-Einstellungen
            </button>
          </nav>
          <div className="footer-socials" aria-label="Social und Kontakt">
            <a href={socialLinks.instagram} target="_blank" rel="noreferrer" aria-label="Instagram">
              <FaInstagram aria-hidden="true" />
            </a>
            <a href={`mailto:${contact.email}`} aria-label="E-Mail">
              <FaEnvelope aria-hidden="true" />
            </a>
            <a href={`https://wa.me/${contact.whatsappLink}`} target="_blank" rel="noreferrer" aria-label="WhatsApp">
              <FaWhatsapp aria-hidden="true" />
            </a>
            <a href={`tel:${contact.phoneLink}`} aria-label="Telefon">
              <FaPhoneAlt aria-hidden="true" />
            </a>
          </div>
        </div>
      </footer>

        <div className="floating-cta" aria-label="Schnellkontakt">
          <a
            className={`floating-btn floating-top ${showBackToTop ? 'floating-top-visible' : ''}`}
            href="#start"
            aria-label="Zur Startseite"
          >
            <FaArrowUp aria-hidden="true" />
          </a>
          <a className="floating-btn floating-phone" href={`tel:${phoneNumber}`} aria-label="Anrufen">
            <FaPhoneAlt aria-hidden="true" /> <span className="floating-label">Anrufen</span>
          </a>
          <a
            className="floating-btn floating-whatsapp"
            href={`https://wa.me/${whatsappNumber}`}
            target="_blank"
            rel="noreferrer"
            aria-label="WhatsApp"
          >
            <FaWhatsapp aria-hidden="true" /> <span className="floating-label">WhatsApp</span>
          </a>
        </div>
      </div>

      <CookieConsent />
    </>
  );
}

export default App;
